(function () {
  'use strict';

  if (window.ArtifySplashCursor) {
    window.ArtifySplashCursor.init(document);
    return;
  }

  var DEFAULTS = {
    SIM_RESOLUTION: 128,
    DYE_RESOLUTION: 1440,
    CAPTURE_RESOLUTION: 512,
    DENSITY_DISSIPATION: 3.5,
    VELOCITY_DISSIPATION: 2,
    PRESSURE: 0.1,
    PRESSURE_ITERATIONS: 20,
    CURL: 3,
    SPLAT_RADIUS: 0.2,
    SPLAT_FORCE: 6000,
    SHADING: true,
    COLOR_UPDATE_SPEED: 10,
    TRANSPARENT: true,
    RAINBOW_MODE: true,
    COLOR: '#ff0000',
  };

  var instances = new WeakMap();
  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function readNumber(root, name, fallback) {
    var raw = root.getAttribute('data-' + name.replace(/[A-Z]/g, function (match) {
      return '-' + match.toLowerCase();
    }));
    if (raw == null || raw === '') return fallback;
    var value = Number(raw);
    return Number.isFinite(value) ? value : fallback;
  }

  function readBoolean(root, name, fallback) {
    var raw = root.getAttribute('data-' + name.replace(/[A-Z]/g, function (match) {
      return '-' + match.toLowerCase();
    }));
    if (raw == null || raw === '') return fallback;
    return raw !== 'false';
  }

  function hexToRgb(hex) {
    var value = String(hex || '#ff0000').replace('#', '');
    if (value.length === 3) value = value[0] + value[0] + value[1] + value[1] + value[2] + value[2];
    return {
      r: (parseInt(value.slice(0, 2), 16) / 255) * 0.15,
      g: (parseInt(value.slice(2, 4), 16) / 255) * 0.15,
      b: (parseInt(value.slice(4, 6), 16) / 255) * 0.15,
    };
  }

  function hsvToRgb(h, s, v) {
    var r;
    var g;
    var b;
    var i = Math.floor(h * 6);
    var f = h * 6 - i;
    var p = v * (1 - s);
    var q = v * (1 - f * s);
    var t = v * (1 - (1 - f) * s);

    switch (i % 6) {
      case 0: r = v; g = t; b = p; break;
      case 1: r = q; g = v; b = p; break;
      case 2: r = p; g = v; b = t; break;
      case 3: r = p; g = q; b = v; break;
      case 4: r = t; g = p; b = v; break;
      case 5: r = v; g = p; b = q; break;
    }

    return { r: r, g: g, b: b };
  }

  function generateColor(config) {
    if (!config.RAINBOW_MODE) return hexToRgb(config.COLOR);
    var color = hsvToRgb(Math.random(), 1, 1);
    color.r *= 0.15;
    color.g *= 0.15;
    color.b *= 0.15;
    return color;
  }

  function scaleByPixelRatio(input) {
    return Math.floor(input * (window.devicePixelRatio || 1));
  }

  function pointerPrototype() {
    this.id = -1;
    this.texcoordX = 0;
    this.texcoordY = 0;
    this.prevTexcoordX = 0;
    this.prevTexcoordY = 0;
    this.deltaX = 0;
    this.deltaY = 0;
    this.down = false;
    this.moved = false;
    this.color = [0, 0, 0];
  }

  function SplashCursor(root) {
    this.root = root;
    this.host = root.querySelector('.hiw-splash');
    if (!this.host || reducedMotion) return;

    this.config = {
      SIM_RESOLUTION: readNumber(root, 'simResolution', DEFAULTS.SIM_RESOLUTION),
      DYE_RESOLUTION: readNumber(root, 'dyeResolution', DEFAULTS.DYE_RESOLUTION),
      CAPTURE_RESOLUTION: readNumber(root, 'captureResolution', DEFAULTS.CAPTURE_RESOLUTION),
      DENSITY_DISSIPATION: readNumber(root, 'densityDissipation', DEFAULTS.DENSITY_DISSIPATION),
      VELOCITY_DISSIPATION: readNumber(root, 'velocityDissipation', DEFAULTS.VELOCITY_DISSIPATION),
      PRESSURE: readNumber(root, 'pressure', DEFAULTS.PRESSURE),
      PRESSURE_ITERATIONS: readNumber(root, 'pressureIterations', DEFAULTS.PRESSURE_ITERATIONS),
      CURL: readNumber(root, 'curl', DEFAULTS.CURL),
      SPLAT_RADIUS: readNumber(root, 'splatRadius', DEFAULTS.SPLAT_RADIUS),
      SPLAT_FORCE: readNumber(root, 'splatForce', DEFAULTS.SPLAT_FORCE),
      SHADING: readBoolean(root, 'shading', DEFAULTS.SHADING),
      COLOR_UPDATE_SPEED: readNumber(root, 'colorUpdateSpeed', DEFAULTS.COLOR_UPDATE_SPEED),
      TRANSPARENT: readBoolean(root, 'transparent', DEFAULTS.TRANSPARENT),
      RAINBOW_MODE: readBoolean(root, 'rainbowMode', DEFAULTS.RAINBOW_MODE),
      COLOR: root.getAttribute('data-color') || DEFAULTS.COLOR,
    };

    this.canvas = document.createElement('canvas');
    this.canvas.className = 'hiw-splash__canvas';
    this.host.appendChild(this.canvas);
    this.pointer = new pointerPrototype();
    this.pointer.color = generateColor(this.config);
    this.isVisible = false;
    this.isActive = true;
    this.animationFrame = 0;
    this.lastUpdateTime = Date.now();
    this.colorUpdateTimer = 0;

    this.initWebGL();
    if (!this.gl || !this.ext || !this.ext.formatRGBA || !this.ext.formatRG || !this.ext.formatR) {
      this.destroy();
      return;
    }

    this.initPrograms();
    this.updateKeywords();
    this.initFramebuffers();
    this.bindHandlers();
    this.observe();
    this.seed();
  }

  SplashCursor.prototype.bindHandlers = function () {
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    window.addEventListener('mousemove', this.handlePointerMove, { passive: true });
    window.addEventListener('mousedown', this.handlePointerDown, { passive: true });
    window.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    window.addEventListener('touchmove', this.handleTouchMove, { passive: true });
  };

  SplashCursor.prototype.observe = function () {
    var self = this;
    if (!('IntersectionObserver' in window)) {
      this.setVisible(true);
      return;
    }
    this.observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.target === self.root) self.setVisible(entry.isIntersecting);
      });
    }, { rootMargin: '35% 0px', threshold: 0.01 });
    this.observer.observe(this.root);
  };

  SplashCursor.prototype.setVisible = function (visible) {
    if (this.isVisible === visible) return;
    this.isVisible = visible;
    if (visible) {
      this.lastUpdateTime = Date.now();
      this.updateFrame();
    } else if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = 0;
    }
  };

  SplashCursor.prototype.seed = function () {
    if (!this.gl) return;
    var points = [
      [0.22, 0.36, 280, -120],
      [0.78, 0.62, -260, 100],
      [0.48, 0.48, 120, 260],
    ];
    for (var i = 0; i < points.length; i++) {
      var color = generateColor(this.config);
      color.r *= 7;
      color.g *= 7;
      color.b *= 7;
      this.splat(points[i][0], points[i][1], points[i][2], points[i][3], color);
    }
  };

  SplashCursor.prototype.initWebGL = function () {
    var canvas = this.canvas;
    var params = { alpha: true, depth: false, stencil: false, antialias: false, preserveDrawingBuffer: false };
    var gl = canvas.getContext('webgl2', params);
    var isWebGL2 = !!gl;
    var halfFloat;
    var supportLinearFiltering;
    if (!isWebGL2) gl = canvas.getContext('webgl', params) || canvas.getContext('experimental-webgl', params);
    if (!gl) return;

    if (isWebGL2) {
      gl.getExtension('EXT_color_buffer_float');
      supportLinearFiltering = gl.getExtension('OES_texture_float_linear');
    } else {
      halfFloat = gl.getExtension('OES_texture_half_float');
      supportLinearFiltering = gl.getExtension('OES_texture_half_float_linear');
    }

    var halfFloatTexType = isWebGL2 ? gl.HALF_FLOAT : halfFloat && halfFloat.HALF_FLOAT_OES;
    this.gl = gl;
    this.ext = {
      formatRGBA: isWebGL2 ? this.getSupportedFormat(gl.RGBA16F, gl.RGBA, halfFloatTexType) : this.getSupportedFormat(gl.RGBA, gl.RGBA, halfFloatTexType),
      formatRG: isWebGL2 ? this.getSupportedFormat(gl.RG16F, gl.RG, halfFloatTexType) : this.getSupportedFormat(gl.RGBA, gl.RGBA, halfFloatTexType),
      formatR: isWebGL2 ? this.getSupportedFormat(gl.R16F, gl.RED, halfFloatTexType) : this.getSupportedFormat(gl.RGBA, gl.RGBA, halfFloatTexType),
      halfFloatTexType: halfFloatTexType,
      supportLinearFiltering: supportLinearFiltering,
    };
    if (!supportLinearFiltering) {
      this.config.DYE_RESOLUTION = 256;
      this.config.SHADING = false;
    }
    gl.clearColor(0, 0, 0, 0);
  };

  SplashCursor.prototype.getSupportedFormat = function (internalFormat, format, type) {
    var gl = this.gl;
    if (!this.supportRenderTextureFormat(internalFormat, format, type)) {
      if (internalFormat === gl.R16F) return this.getSupportedFormat(gl.RG16F, gl.RG, type);
      if (internalFormat === gl.RG16F) return this.getSupportedFormat(gl.RGBA16F, gl.RGBA, type);
      return null;
    }
    return { internalFormat: internalFormat, format: format };
  };

  SplashCursor.prototype.supportRenderTextureFormat = function (internalFormat, format, type) {
    var gl = this.gl;
    var texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texImage2D(gl.TEXTURE_2D, 0, internalFormat, 4, 4, 0, format, type, null);
    var fbo = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
    return gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE;
  };

  SplashCursor.prototype.compileShader = function (type, source, keywords) {
    var gl = this.gl;
    if (keywords) source = keywords.map(function (keyword) { return '#define ' + keyword + '\n'; }).join('') + source;
    var shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    return shader;
  };

  SplashCursor.prototype.createProgram = function (vertexShader, fragmentShader) {
    var gl = this.gl;
    var program = gl.createProgram();
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    return program;
  };

  SplashCursor.prototype.getUniforms = function (program) {
    var gl = this.gl;
    var uniforms = {};
    var count = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS);
    for (var i = 0; i < count; i++) {
      var name = gl.getActiveUniform(program, i).name;
      uniforms[name] = gl.getUniformLocation(program, name);
    }
    return uniforms;
  };

  SplashCursor.prototype.createProgramObject = function (fragmentShader) {
    var self = this;
    var program = this.createProgram(this.baseVertexShader, fragmentShader);
    return {
      uniforms: self.getUniforms(program),
      bind: function () {
        self.gl.useProgram(program);
      },
    };
  };

  SplashCursor.prototype.initPrograms = function () {
    var gl = this.gl;
    this.baseVertexShader = this.compileShader(gl.VERTEX_SHADER, [
      'precision highp float;',
      'attribute vec2 aPosition;',
      'varying vec2 vUv;',
      'varying vec2 vL;',
      'varying vec2 vR;',
      'varying vec2 vT;',
      'varying vec2 vB;',
      'uniform vec2 texelSize;',
      'void main(){',
      'vUv=aPosition*0.5+0.5;',
      'vL=vUv-vec2(texelSize.x,0.0);',
      'vR=vUv+vec2(texelSize.x,0.0);',
      'vT=vUv+vec2(0.0,texelSize.y);',
      'vB=vUv-vec2(0.0,texelSize.y);',
      'gl_Position=vec4(aPosition,0.0,1.0);',
      '}',
    ].join('\n'));

    var copyShader = this.compileShader(gl.FRAGMENT_SHADER, 'precision mediump float;precision mediump sampler2D;varying highp vec2 vUv;uniform sampler2D uTexture;void main(){gl_FragColor=texture2D(uTexture,vUv);}');
    var clearShader = this.compileShader(gl.FRAGMENT_SHADER, 'precision mediump float;precision mediump sampler2D;varying highp vec2 vUv;uniform sampler2D uTexture;uniform float value;void main(){gl_FragColor=value*texture2D(uTexture,vUv);}');
    var splatShader = this.compileShader(gl.FRAGMENT_SHADER, 'precision highp float;precision highp sampler2D;varying vec2 vUv;uniform sampler2D uTarget;uniform float aspectRatio;uniform vec3 color;uniform vec2 point;uniform float radius;void main(){vec2 p=vUv-point.xy;p.x*=aspectRatio;vec3 splat=exp(-dot(p,p)/radius)*color;vec3 base=texture2D(uTarget,vUv).xyz;gl_FragColor=vec4(base+splat,1.0);}');
    var advectionShader = this.compileShader(gl.FRAGMENT_SHADER, 'precision highp float;precision highp sampler2D;varying vec2 vUv;uniform sampler2D uVelocity;uniform sampler2D uSource;uniform vec2 texelSize;uniform vec2 dyeTexelSize;uniform float dt;uniform float dissipation;vec4 bilerp(sampler2D sam,vec2 uv,vec2 tsize){vec2 st=uv/tsize-0.5;vec2 iuv=floor(st);vec2 fuv=fract(st);vec4 a=texture2D(sam,(iuv+vec2(0.5,0.5))*tsize);vec4 b=texture2D(sam,(iuv+vec2(1.5,0.5))*tsize);vec4 c=texture2D(sam,(iuv+vec2(0.5,1.5))*tsize);vec4 d=texture2D(sam,(iuv+vec2(1.5,1.5))*tsize);return mix(mix(a,b,fuv.x),mix(c,d,fuv.x),fuv.y);}void main(){\n#ifdef MANUAL_FILTERING\nvec2 coord=vUv-dt*bilerp(uVelocity,vUv,texelSize).xy*texelSize;vec4 result=bilerp(uSource,coord,dyeTexelSize);\n#else\nvec2 coord=vUv-dt*texture2D(uVelocity,vUv).xy*texelSize;vec4 result=texture2D(uSource,coord);\n#endif\nfloat decay=1.0+dissipation*dt;gl_FragColor=result/decay;}', this.ext.supportLinearFiltering ? null : ['MANUAL_FILTERING']);
    var divergenceShader = this.compileShader(gl.FRAGMENT_SHADER, 'precision mediump float;precision mediump sampler2D;varying highp vec2 vUv;varying highp vec2 vL;varying highp vec2 vR;varying highp vec2 vT;varying highp vec2 vB;uniform sampler2D uVelocity;void main(){float L=texture2D(uVelocity,vL).x;float R=texture2D(uVelocity,vR).x;float T=texture2D(uVelocity,vT).y;float B=texture2D(uVelocity,vB).y;vec2 C=texture2D(uVelocity,vUv).xy;if(vL.x<0.0){L=-C.x;}if(vR.x>1.0){R=-C.x;}if(vT.y>1.0){T=-C.y;}if(vB.y<0.0){B=-C.y;}float div=0.5*(R-L+T-B);gl_FragColor=vec4(div,0.0,0.0,1.0);}');
    var curlShader = this.compileShader(gl.FRAGMENT_SHADER, 'precision mediump float;precision mediump sampler2D;varying highp vec2 vUv;varying highp vec2 vL;varying highp vec2 vR;varying highp vec2 vT;varying highp vec2 vB;uniform sampler2D uVelocity;void main(){float L=texture2D(uVelocity,vL).y;float R=texture2D(uVelocity,vR).y;float T=texture2D(uVelocity,vT).x;float B=texture2D(uVelocity,vB).x;float vorticity=R-L-T+B;gl_FragColor=vec4(0.5*vorticity,0.0,0.0,1.0);}');
    var vorticityShader = this.compileShader(gl.FRAGMENT_SHADER, 'precision highp float;precision highp sampler2D;varying vec2 vUv;varying vec2 vL;varying vec2 vR;varying vec2 vT;varying vec2 vB;uniform sampler2D uVelocity;uniform sampler2D uCurl;uniform float curl;uniform float dt;void main(){float L=texture2D(uCurl,vL).x;float R=texture2D(uCurl,vR).x;float T=texture2D(uCurl,vT).x;float B=texture2D(uCurl,vB).x;float C=texture2D(uCurl,vUv).x;vec2 force=0.5*vec2(abs(T)-abs(B),abs(R)-abs(L));force/=length(force)+0.0001;force*=curl*C;force.y*=-1.0;vec2 velocity=texture2D(uVelocity,vUv).xy;velocity+=force*dt;velocity=min(max(velocity,-1000.0),1000.0);gl_FragColor=vec4(velocity,0.0,1.0);}');
    var pressureShader = this.compileShader(gl.FRAGMENT_SHADER, 'precision mediump float;precision mediump sampler2D;varying highp vec2 vUv;varying highp vec2 vL;varying highp vec2 vR;varying highp vec2 vT;varying highp vec2 vB;uniform sampler2D uPressure;uniform sampler2D uDivergence;void main(){float L=texture2D(uPressure,vL).x;float R=texture2D(uPressure,vR).x;float T=texture2D(uPressure,vT).x;float B=texture2D(uPressure,vB).x;float divergence=texture2D(uDivergence,vUv).x;float pressure=(L+R+B+T-divergence)*0.25;gl_FragColor=vec4(pressure,0.0,0.0,1.0);}');
    var gradientSubtractShader = this.compileShader(gl.FRAGMENT_SHADER, 'precision mediump float;precision mediump sampler2D;varying highp vec2 vUv;varying highp vec2 vL;varying highp vec2 vR;varying highp vec2 vT;varying highp vec2 vB;uniform sampler2D uPressure;uniform sampler2D uVelocity;void main(){float L=texture2D(uPressure,vL).x;float R=texture2D(uPressure,vR).x;float T=texture2D(uPressure,vT).x;float B=texture2D(uPressure,vB).x;vec2 velocity=texture2D(uVelocity,vUv).xy;velocity.xy-=vec2(R-L,T-B);gl_FragColor=vec4(velocity,0.0,1.0);}');
    this.displayShaderSource = 'precision highp float;precision highp sampler2D;varying vec2 vUv;varying vec2 vL;varying vec2 vR;varying vec2 vT;varying vec2 vB;uniform sampler2D uTexture;uniform vec2 texelSize;void main(){vec3 c=texture2D(uTexture,vUv).rgb;\n#ifdef SHADING\nvec3 lc=texture2D(uTexture,vL).rgb;vec3 rc=texture2D(uTexture,vR).rgb;vec3 tc=texture2D(uTexture,vT).rgb;vec3 bc=texture2D(uTexture,vB).rgb;float dx=length(rc)-length(lc);float dy=length(tc)-length(bc);vec3 n=normalize(vec3(dx,dy,length(texelSize)));vec3 l=vec3(0.0,0.0,1.0);float diffuse=clamp(dot(n,l)+0.7,0.7,1.0);c*=diffuse;\n#endif\nfloat a=max(c.r,max(c.g,c.b));gl_FragColor=vec4(c,a);}';

    this.copyProgram = this.createProgramObject(copyShader);
    this.clearProgram = this.createProgramObject(clearShader);
    this.splatProgram = this.createProgramObject(splatShader);
    this.advectionProgram = this.createProgramObject(advectionShader);
    this.divergenceProgram = this.createProgramObject(divergenceShader);
    this.curlProgram = this.createProgramObject(curlShader);
    this.vorticityProgram = this.createProgramObject(vorticityShader);
    this.pressureProgram = this.createProgramObject(pressureShader);
    this.gradientSubtractProgram = this.createProgramObject(gradientSubtractShader);

    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, -1, 1, 1, 1, 1, -1]), gl.STATIC_DRAW);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array([0, 1, 2, 0, 2, 3]), gl.STATIC_DRAW);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.enableVertexAttribArray(0);
  };

  SplashCursor.prototype.updateKeywords = function () {
    var fragmentShader = this.compileShader(this.gl.FRAGMENT_SHADER, this.displayShaderSource, this.config.SHADING ? ['SHADING'] : null);
    var program = this.createProgram(this.baseVertexShader, fragmentShader);
    this.displayProgram = {
      uniforms: this.getUniforms(program),
      bind: function () {
        this.gl.useProgram(program);
      }.bind(this),
    };
  };

  SplashCursor.prototype.blit = function (target, clear) {
    var gl = this.gl;
    if (target == null) {
      gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    } else {
      gl.viewport(0, 0, target.width, target.height);
      gl.bindFramebuffer(gl.FRAMEBUFFER, target.fbo);
    }
    if (clear) {
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
    }
    gl.drawElements(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0);
  };

  SplashCursor.prototype.getResolution = function (resolution) {
    var gl = this.gl;
    var aspectRatio = gl.drawingBufferWidth / gl.drawingBufferHeight;
    if (aspectRatio < 1) aspectRatio = 1 / aspectRatio;
    var min = Math.round(resolution);
    var max = Math.round(resolution * aspectRatio);
    return gl.drawingBufferWidth > gl.drawingBufferHeight ? { width: max, height: min } : { width: min, height: max };
  };

  SplashCursor.prototype.createFBO = function (w, h, internalFormat, format, type, param) {
    var gl = this.gl;
    gl.activeTexture(gl.TEXTURE0);
    var texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, param);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, param);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texImage2D(gl.TEXTURE_2D, 0, internalFormat, w, h, 0, format, type, null);
    var fbo = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
    gl.viewport(0, 0, w, h);
    gl.clear(gl.COLOR_BUFFER_BIT);
    return {
      texture: texture,
      fbo: fbo,
      width: w,
      height: h,
      texelSizeX: 1 / w,
      texelSizeY: 1 / h,
      attach: function (id) {
        gl.activeTexture(gl.TEXTURE0 + id);
        gl.bindTexture(gl.TEXTURE_2D, texture);
        return id;
      },
    };
  };

  SplashCursor.prototype.createDoubleFBO = function (w, h, internalFormat, format, type, param) {
    var fbo1 = this.createFBO(w, h, internalFormat, format, type, param);
    var fbo2 = this.createFBO(w, h, internalFormat, format, type, param);
    return {
      width: w,
      height: h,
      texelSizeX: fbo1.texelSizeX,
      texelSizeY: fbo1.texelSizeY,
      get read() { return fbo1; },
      set read(value) { fbo1 = value; },
      get write() { return fbo2; },
      set write(value) { fbo2 = value; },
      swap: function () {
        var temp = fbo1;
        fbo1 = fbo2;
        fbo2 = temp;
      },
    };
  };

  SplashCursor.prototype.initFramebuffers = function () {
    var simRes = this.getResolution(this.config.SIM_RESOLUTION);
    var dyeRes = this.getResolution(this.config.DYE_RESOLUTION);
    var ext = this.ext;
    var filtering = ext.supportLinearFiltering ? this.gl.LINEAR : this.gl.NEAREST;
    this.gl.disable(this.gl.BLEND);
    this.dye = this.createDoubleFBO(dyeRes.width, dyeRes.height, ext.formatRGBA.internalFormat, ext.formatRGBA.format, ext.halfFloatTexType, filtering);
    this.velocity = this.createDoubleFBO(simRes.width, simRes.height, ext.formatRG.internalFormat, ext.formatRG.format, ext.halfFloatTexType, filtering);
    this.divergence = this.createFBO(simRes.width, simRes.height, ext.formatR.internalFormat, ext.formatR.format, ext.halfFloatTexType, this.gl.NEAREST);
    this.curl = this.createFBO(simRes.width, simRes.height, ext.formatR.internalFormat, ext.formatR.format, ext.halfFloatTexType, this.gl.NEAREST);
    this.pressure = this.createDoubleFBO(simRes.width, simRes.height, ext.formatR.internalFormat, ext.formatR.format, ext.halfFloatTexType, this.gl.NEAREST);
  };

  SplashCursor.prototype.resizeCanvas = function () {
    var width = Math.max(1, scaleByPixelRatio(this.canvas.clientWidth));
    var height = Math.max(1, scaleByPixelRatio(this.canvas.clientHeight));
    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width;
      this.canvas.height = height;
      this.initFramebuffers();
      return true;
    }
    return false;
  };

  SplashCursor.prototype.calcDeltaTime = function () {
    var now = Date.now();
    var dt = (now - this.lastUpdateTime) / 1000;
    this.lastUpdateTime = now;
    return Math.min(dt, 0.016666);
  };

  SplashCursor.prototype.updateFrame = function () {
    if (!this.isActive || !this.isVisible) return;
    var dt = this.calcDeltaTime();
    this.resizeCanvas();
    this.colorUpdateTimer += dt * this.config.COLOR_UPDATE_SPEED;
    if (this.colorUpdateTimer >= 1) {
      this.colorUpdateTimer = 0;
      this.pointer.color = generateColor(this.config);
    }
    if (this.pointer.moved) {
      this.pointer.moved = false;
      this.splatPointer(this.pointer);
    }
    this.step(dt);
    this.render(null);
    this.animationFrame = requestAnimationFrame(this.updateFrame.bind(this));
  };

  SplashCursor.prototype.step = function (dt) {
    var gl = this.gl;
    gl.disable(gl.BLEND);
    this.curlProgram.bind();
    gl.uniform2f(this.curlProgram.uniforms.texelSize, this.velocity.texelSizeX, this.velocity.texelSizeY);
    gl.uniform1i(this.curlProgram.uniforms.uVelocity, this.velocity.read.attach(0));
    this.blit(this.curl);

    this.vorticityProgram.bind();
    gl.uniform2f(this.vorticityProgram.uniforms.texelSize, this.velocity.texelSizeX, this.velocity.texelSizeY);
    gl.uniform1i(this.vorticityProgram.uniforms.uVelocity, this.velocity.read.attach(0));
    gl.uniform1i(this.vorticityProgram.uniforms.uCurl, this.curl.attach(1));
    gl.uniform1f(this.vorticityProgram.uniforms.curl, this.config.CURL);
    gl.uniform1f(this.vorticityProgram.uniforms.dt, dt);
    this.blit(this.velocity.write);
    this.velocity.swap();

    this.divergenceProgram.bind();
    gl.uniform2f(this.divergenceProgram.uniforms.texelSize, this.velocity.texelSizeX, this.velocity.texelSizeY);
    gl.uniform1i(this.divergenceProgram.uniforms.uVelocity, this.velocity.read.attach(0));
    this.blit(this.divergence);

    this.clearProgram.bind();
    gl.uniform1i(this.clearProgram.uniforms.uTexture, this.pressure.read.attach(0));
    gl.uniform1f(this.clearProgram.uniforms.value, this.config.PRESSURE);
    this.blit(this.pressure.write);
    this.pressure.swap();

    this.pressureProgram.bind();
    gl.uniform2f(this.pressureProgram.uniforms.texelSize, this.velocity.texelSizeX, this.velocity.texelSizeY);
    gl.uniform1i(this.pressureProgram.uniforms.uDivergence, this.divergence.attach(0));
    for (var i = 0; i < this.config.PRESSURE_ITERATIONS; i++) {
      gl.uniform1i(this.pressureProgram.uniforms.uPressure, this.pressure.read.attach(1));
      this.blit(this.pressure.write);
      this.pressure.swap();
    }

    this.gradientSubtractProgram.bind();
    gl.uniform2f(this.gradientSubtractProgram.uniforms.texelSize, this.velocity.texelSizeX, this.velocity.texelSizeY);
    gl.uniform1i(this.gradientSubtractProgram.uniforms.uPressure, this.pressure.read.attach(0));
    gl.uniform1i(this.gradientSubtractProgram.uniforms.uVelocity, this.velocity.read.attach(1));
    this.blit(this.velocity.write);
    this.velocity.swap();

    this.advectionProgram.bind();
    gl.uniform2f(this.advectionProgram.uniforms.texelSize, this.velocity.texelSizeX, this.velocity.texelSizeY);
    if (!this.ext.supportLinearFiltering) gl.uniform2f(this.advectionProgram.uniforms.dyeTexelSize, this.velocity.texelSizeX, this.velocity.texelSizeY);
    var velocityId = this.velocity.read.attach(0);
    gl.uniform1i(this.advectionProgram.uniforms.uVelocity, velocityId);
    gl.uniform1i(this.advectionProgram.uniforms.uSource, velocityId);
    gl.uniform1f(this.advectionProgram.uniforms.dt, dt);
    gl.uniform1f(this.advectionProgram.uniforms.dissipation, this.config.VELOCITY_DISSIPATION);
    this.blit(this.velocity.write);
    this.velocity.swap();

    if (!this.ext.supportLinearFiltering) gl.uniform2f(this.advectionProgram.uniforms.dyeTexelSize, this.dye.texelSizeX, this.dye.texelSizeY);
    gl.uniform1i(this.advectionProgram.uniforms.uVelocity, this.velocity.read.attach(0));
    gl.uniform1i(this.advectionProgram.uniforms.uSource, this.dye.read.attach(1));
    gl.uniform1f(this.advectionProgram.uniforms.dissipation, this.config.DENSITY_DISSIPATION);
    this.blit(this.dye.write);
    this.dye.swap();
  };

  SplashCursor.prototype.render = function (target) {
    var gl = this.gl;
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.enable(gl.BLEND);
    this.displayProgram.bind();
    if (this.config.SHADING) {
      var width = target == null ? gl.drawingBufferWidth : target.width;
      var height = target == null ? gl.drawingBufferHeight : target.height;
      gl.uniform2f(this.displayProgram.uniforms.texelSize, 1 / width, 1 / height);
    }
    gl.uniform1i(this.displayProgram.uniforms.uTexture, this.dye.read.attach(0));
    this.blit(target);
  };

  SplashCursor.prototype.correctRadius = function (radius) {
    var aspectRatio = this.canvas.width / this.canvas.height;
    if (aspectRatio > 1) radius *= aspectRatio;
    return radius;
  };

  SplashCursor.prototype.splat = function (x, y, dx, dy, color) {
    var gl = this.gl;
    this.splatProgram.bind();
    gl.uniform1i(this.splatProgram.uniforms.uTarget, this.velocity.read.attach(0));
    gl.uniform1f(this.splatProgram.uniforms.aspectRatio, this.canvas.width / this.canvas.height);
    gl.uniform2f(this.splatProgram.uniforms.point, x, y);
    gl.uniform3f(this.splatProgram.uniforms.color, dx, dy, 0);
    gl.uniform1f(this.splatProgram.uniforms.radius, this.correctRadius(this.config.SPLAT_RADIUS / 100));
    this.blit(this.velocity.write);
    this.velocity.swap();
    gl.uniform1i(this.splatProgram.uniforms.uTarget, this.dye.read.attach(0));
    gl.uniform3f(this.splatProgram.uniforms.color, color.r, color.g, color.b);
    this.blit(this.dye.write);
    this.dye.swap();
  };

  SplashCursor.prototype.splatPointer = function (pointer) {
    this.splat(pointer.texcoordX, pointer.texcoordY, pointer.deltaX * this.config.SPLAT_FORCE, pointer.deltaY * this.config.SPLAT_FORCE, pointer.color);
  };

  SplashCursor.prototype.updatePointer = function (clientX, clientY, force) {
    var rect = this.canvas.getBoundingClientRect();
    if (clientX < rect.left || clientX > rect.right || clientY < rect.top || clientY > rect.bottom) return;
    var pointer = this.pointer;
    var x = scaleByPixelRatio(clientX - rect.left);
    var y = scaleByPixelRatio(clientY - rect.top);
    var tx = x / this.canvas.width;
    var ty = 1 - y / this.canvas.height;
    var aspectRatio = this.canvas.width / this.canvas.height;
    pointer.prevTexcoordX = pointer.texcoordX || tx;
    pointer.prevTexcoordY = pointer.texcoordY || ty;
    pointer.texcoordX = tx;
    pointer.texcoordY = ty;
    pointer.deltaX = tx - pointer.prevTexcoordX;
    pointer.deltaY = ty - pointer.prevTexcoordY;
    if (aspectRatio < 1) pointer.deltaX *= aspectRatio;
    if (aspectRatio > 1) pointer.deltaY /= aspectRatio;
    pointer.moved = force || Math.abs(pointer.deltaX) > 0 || Math.abs(pointer.deltaY) > 0;
  };

  SplashCursor.prototype.handlePointerMove = function (event) {
    this.updatePointer(event.clientX, event.clientY, false);
  };

  SplashCursor.prototype.handlePointerDown = function (event) {
    this.pointer.color = generateColor(this.config);
    this.updatePointer(event.clientX, event.clientY, true);
  };

  SplashCursor.prototype.handleTouchStart = function (event) {
    if (!event.targetTouches || !event.targetTouches.length) return;
    this.pointer.color = generateColor(this.config);
    this.updatePointer(event.targetTouches[0].clientX, event.targetTouches[0].clientY, true);
  };

  SplashCursor.prototype.handleTouchMove = function (event) {
    if (!event.targetTouches || !event.targetTouches.length) return;
    this.updatePointer(event.targetTouches[0].clientX, event.targetTouches[0].clientY, false);
  };

  SplashCursor.prototype.destroy = function () {
    this.isActive = false;
    if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('mousemove', this.handlePointerMove);
    window.removeEventListener('mousedown', this.handlePointerDown);
    window.removeEventListener('touchstart', this.handleTouchStart);
    window.removeEventListener('touchmove', this.handleTouchMove);
    if (this.canvas && this.canvas.parentNode) this.canvas.parentNode.removeChild(this.canvas);
  };

  function init(root) {
    var scope = root || document;
    var nodes = scope.matches && scope.matches('[data-hiw-splash]') ? [scope] : Array.prototype.slice.call(scope.querySelectorAll('[data-hiw-splash]'));
    nodes.forEach(function (node) {
      if (instances.has(node)) return;
      var instance = new SplashCursor(node);
      if (instance && instance.canvas) instances.set(node, instance);
    });
  }

  function destroy(root) {
    var scope = root || document;
    var nodes = scope.matches && scope.matches('[data-hiw-splash]') ? [scope] : Array.prototype.slice.call(scope.querySelectorAll('[data-hiw-splash]'));
    nodes.forEach(function (node) {
      var instance = instances.get(node);
      if (!instance) return;
      instance.destroy();
      instances.delete(node);
    });
  }

  window.ArtifySplashCursor = { init: init, destroy: destroy };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
  document.addEventListener('shopify:section:select', function (event) { init(event.target); });
  document.addEventListener('shopify:section:unload', function (event) { destroy(event.target); });
}());
